import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Plus, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRODUCTS = [
  { value: "insurance",   label: "Insurance" },
  { value: "mutual_fund", label: "Mutual Fund" },
  { value: "loan",        label: "Loan" },
  { value: "retirement",  label: "Retirement" },
];
const STATUSES = [
  { value: "new",       label: "New",       color: { bg: "#E0F2FE", fg: "#0284C7" } },
  { value: "kyc",       label: "KYC",       color: { bg: "#FEF3C7", fg: "#B45309" } },
  { value: "proposal",  label: "Proposal",  color: { bg: "#EDE9FE", fg: "#6D28D9" } },
  { value: "onboarded", label: "Onboarded", color: { bg: "#DCFCE7", fg: "#15803D" } },
  { value: "renewal",   label: "Renewal",   color: { bg: "#DBEAFE", fg: "#1D4ED8" } },
  { value: "closed",    label: "Closed",    color: { bg: "#F1F5F9", fg: "#475569" } },
];
const LOCKED = new Set(["proposal", "onboarded", "renewal"]);

export default function Wealth() {
  const [items, setItems] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [productFilter, setProductFilter] = useState("all");

  const load = async () => {
    const params = productFilter === "all" ? {} : { product_type: productFilter };
    setItems((await api.get("/wealth/enquiries", { params })).data);
  };
  useEffect(() => {
    load();
    api.get("/wealth/disclaimer").then(r => setDisclaimer(r.data.disclaimer));
    // eslint-disable-next-line
  }, [productFilter]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/wealth/enquiries/${id}`, body);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/wealth/enquiries/${id}`);
    load();
  };

  const productLabel = (v) => PRODUCTS.find(p => p.value === v)?.label || v;
  const statusMeta = (v) => STATUSES.find(s => s.value === v) || STATUSES[0];

  return (
    <AppLayout title="Wealth">
      {/* Mandatory disclaimer banner */}
      <div data-testid="wealth-disclaimer-banner"
           className="rounded-xl border-l-4 border-l-[var(--miro-blue)] bg-white border border-[var(--miro-border)] p-4 mb-5 flex items-start gap-3">
        <ShieldAlert size={18} className="text-[var(--miro-blue-600)] mt-0.5 flex-shrink-0"/>
        <div className="text-xs text-[var(--miro-text-secondary)] leading-relaxed">
          <div className="font-semibold text-[var(--miro-navy)] mb-1 text-sm">Workflow-only · Not investment advice</div>
          {disclaimer || "Loading disclaimer…"}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-5">
        <Select value={productFilter} onValueChange={setProductFilter}>
          <SelectTrigger data-testid="wealth-filter-product" className="w-44 bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {PRODUCTS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto">
          <NewEnquiryDialog open={newOpen} setOpen={setNewOpen} onCreated={load}/>
        </div>
      </div>

      <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-hidden">
        <table data-testid="wealth-table" className="w-full text-sm">
          <thead className="bg-[var(--miro-bg)] text-[var(--miro-text-secondary)]">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Client</th>
              <th className="px-4 py-3 font-semibold">Product</th>
              <th className="px-4 py-3 font-semibold">Amount</th>
              <th className="px-4 py-3 font-semibold">Disclaimer</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[var(--miro-text-muted)]">No enquiries yet.</td></tr>
            )}
            {items.map(e => {
              const st = statusMeta(e.status);
              const needsAck = LOCKED.has(e.status) || (!e.disclaimer_ack);
              return (
                <tr key={e.id} data-testid={`wealth-row-${e.id}`} className="border-t border-[var(--miro-border)]">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[var(--miro-navy)]">{e.name}</div>
                    <div className="text-xs text-[var(--miro-text-muted)]">{e.sub_product || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className="border-0 font-semibold" style={{ background: "#E0F2FE", color: "#0284C7" }}>{productLabel(e.product_type)}</Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-[var(--miro-navy)]">₹{(e.amount || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <Checkbox data-testid={`wealth-ack-${e.id}`} checked={!!e.disclaimer_ack}
                                onCheckedChange={(v) => patch(e.id, { disclaimer_ack: !!v })}/>
                      <span className={e.disclaimer_ack ? "text-[var(--miro-green-600)] font-semibold" : (needsAck ? "text-red-600 font-semibold" : "text-[var(--miro-text-muted)]")}>
                        {e.disclaimer_ack ? "Acknowledged" : "Ack required"}
                      </span>
                    </label>
                  </td>
                  <td className="px-4 py-3">
                    <Select value={e.status} onValueChange={(v) => patch(e.id, { status: v })}>
                      <SelectTrigger data-testid={`wealth-status-${e.id}`} className="h-8 w-32 text-xs" style={{ background: st.color.bg, color: st.color.fg, border: "none" }}>
                        <SelectValue/>
                      </SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`wealth-delete-${e.id}`} onClick={() => remove(e.id)}
                            className="text-[var(--miro-text-muted)] hover:text-red-600">
                      <Trash2 size={15}/>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}

function NewEnquiryDialog({ open, setOpen, onCreated }) {
  const [f, setF] = useState({ name: "", product_type: "insurance", sub_product: "", amount: 0, status: "new", disclaimer_ack: false, notes: "" });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/wealth/enquiries", { ...f, amount: Number(f.amount) || 0 });
      toast.success("Enquiry created");
      setOpen(false);
      setF({ name: "", product_type: "insurance", sub_product: "", amount: 0, status: "new", disclaimer_ack: false, notes: "" });
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-wealth-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New enquiry</Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New wealth enquiry</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Client name *</Label><Input data-testid="new-wealth-name" value={f.name} onChange={e=>upd("name",e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Product</Label>
            <Select value={f.product_type} onValueChange={v=>upd("product_type",v)}>
              <SelectTrigger data-testid="new-wealth-product" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{PRODUCTS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Sub-product</Label><Input data-testid="new-wealth-sub" placeholder="Term, Health, Equity SIP…" value={f.sub_product} onChange={e=>upd("sub_product",e.target.value)} className="mt-1"/></div>
          <div><Label>Amount (₹)</Label><Input data-testid="new-wealth-amount" type="number" value={f.amount} onChange={e=>upd("amount",e.target.value)} className="mt-1"/></div>
          <div>
            <Label>Status</Label>
            <Select value={f.status} onValueChange={v=>upd("status",v)}>
              <SelectTrigger data-testid="new-wealth-status" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="col-span-2 flex items-start gap-3 rounded-lg bg-[var(--miro-bg)] border border-[var(--miro-border)] p-3 cursor-pointer">
            <Checkbox data-testid="new-wealth-ack" checked={f.disclaimer_ack} onCheckedChange={v => upd("disclaimer_ack", !!v)}/>
            <span className="text-xs text-[var(--miro-text-secondary)] leading-snug">
              I confirm I have shared the MIRO Wealth workflow-only disclaimer with this client and they understand
              MIRO does not provide investment, insurance, tax or legal advice.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid="new-wealth-submit"
                  disabled={!f.name.trim() || saving || (LOCKED.has(f.status) && !f.disclaimer_ack)}
                  onClick={submit} style={{ background: "var(--miro-navy)" }}>
            {saving ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
